import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  getCustomerTypes,
  getAllCustomers,
  getSalesExecutives,
} from '../services/customer_service'
import { getPayments, getTotalPayments } from '../services/payment_service.js'
import './Payment.css'

const orderTypes = [
  { value: 'published_book', label: 'Publish Books' },
  { value: 'exercise_book', label: 'Exercise Books' },
]
const paymentMethods = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Mobile Money', label: 'Mobile Money' },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
]

const emptyFilters = {
  fromDate: '',
  toDate: '',
  fromAmount: '',
  toAmount: '',
  customer: '',
  customerType: '',
  orderType: '',
  salesExecutive: '',
  paymentMethod: '',
}

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getOptionValue = (option) =>
  typeof option === 'object'
    ? getValue(option, ['id', 'value', 'customerid', 'customer_id', 'userid'])
    : option
const getOptionLabel = (option) =>
  typeof option === 'object'
    ? getValue(option, [
        'name',
        'label',
        'cust_name',
        'customer_name',
        'sales_exec_name',
        'sales_executive',
        'customertype',
        'type',
      ])
    : option
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)
const getPaymentId = (payment) =>
  getValue(payment, ['order_paymentid', 'payment_id', 'paymentid', 'id'])
const getPaymentDate = (payment) =>
  getValue(payment, ['payment_date', 'pay_date', 'date'])
const getPaymentAmount = (payment) =>
  getValue(payment, ['total_amount', 'payment_amount', 'amount'], 0)
const getPaymentMode = (payment) =>
  getValue(payment, ['pay_mode', 'payment_mode', 'mode'])
const getPaymentOrderId = (payment) =>
  getValue(payment, ['orderid', 'order_id'])
const getPaymentOrderType = (payment) => {
  const type = getValue(payment, ['order_type', 'orderType'], '')
  const match = orderTypes.find((option) => option.value === type)
  return match ? match.label : type
}

function Payment() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState(emptyFilters)
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
  const [isQueryOpen, setIsQueryOpen] = useState(false)
  const [payments, setPayments] = useState([])
  const [salesExecutives, setSalesExecutives] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [customers, setCustomers] = useState([])
  const [totalPayments, setTotalPayments] = useState(0)
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const loadPayments = async (
    cursor = null,
    page = 1,
    query = appliedFilters,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getPayments({ cursor, page, ...query })
      setPayments(result.payments)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    Promise.all([
      getSalesExecutives(),
      getCustomerTypes(),
      getAllCustomers(),
      getTotalPayments(),
    ])
      .then(
        ([
          loadedSalesExecutives,
          loadedCustomerTypes,
          loadedCustomers,
          loadedTotalPayments,
        ]) => {
          setSalesExecutives(loadedSalesExecutives)
          setCustomerTypes(loadedCustomerTypes)
          setCustomers(loadedCustomers)
          setTotalPayments(loadedTotalPayments)
        },
      )
      .catch((requestError) => setError(requestError.message))
    loadPayments(null, 1, emptyFilters)
  }, [])

  const updateFilter = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({
      ...current,
      [name]: value,
      ...(name === 'customerType' ? { customer: '' } : {}),
    }))
  }
  const handleCustomerTypeChange = async (event) => {
    const customerType = event.target.value
    setFilters((current) => ({
      ...current,
      customerType,
      customer: '',
    }))
    setError('')

    try {
      setCustomers(await getAllCustomers(customerType))
    } catch (requestError) {
      setCustomers([])
      setError(requestError.message)
    }
  }
  const searchPayments = (event) => {
    event.preventDefault()
    setAppliedFilters(filters)
    setCursorHistory([])
    loadPayments(null, 1, filters)
  }
  const clearFilters = () => {
    setFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setCursorHistory([])
    loadPayments(null, 1, emptyFilters)
  }
  const goToNextPage = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadPayments(nextCursor, nextPage || currentPage, filters)
  }
  const goToPreviousPage = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadPayments(previous.cursor, previous.page, filters)
  }

  return (
    <div className='payments-page'>
      <nav className='payments-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Payments</strong>
      </nav>
      <header className='payments-page__header'>
        <div>
          <span className='app-content__eyebrow'>Sales</span>
          <h1>Payments</h1>
          <p>
            Review recorded payments and narrow the list by date, value, and
            account.
          </p>
        </div>
      </header>
      {error && (
        <div className='payments-page__error' role='alert'>
          {error}
        </div>
      )}

      <section
        className='payments-query-card'
        aria-labelledby='payments-query-title'
      >
        <button
          className='payments-query-card__toggle'
          type='button'
          onClick={() => setIsQueryOpen((open) => !open)}
          aria-expanded={isQueryOpen}
        >
          <span>
            <span className='app-content__eyebrow'>Refine results</span>
            <strong id='payments-query-title'>Payment query</strong>
          </span>
          <span aria-hidden='true'>{isQueryOpen ? '−' : '+'}</span>
        </button>
        {isQueryOpen && (
          <form className='payments-query-form' onSubmit={searchPayments}>
            <fieldset className='payments-query-form__range'>
              <legend>Date range</legend>
              <label>
                From date
                <input
                  name='fromDate'
                  type='date'
                  value={filters.fromDate}
                  onChange={updateFilter}
                />
              </label>
              <label>
                To date
                <input
                  name='toDate'
                  type='date'
                  value={filters.toDate}
                  onChange={updateFilter}
                />
              </label>
            </fieldset>
            <fieldset className='payments-query-form__range'>
              <legend>Amount range</legend>
              <label>
                From amount
                <input
                  name='fromAmount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={filters.fromAmount}
                  onChange={updateFilter}
                />
              </label>
              <label>
                To amount
                <input
                  name='toAmount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={filters.toAmount}
                  onChange={updateFilter}
                />
              </label>
            </fieldset>
            <label>
              Customer type
              <select
                name='customerType'
                value={filters.customerType}
                onChange={handleCustomerTypeChange}
              >
                <option value=''>All customer types</option>
                {customerTypes.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Customer
              <select
                name='customer'
                value={filters.customer}
                onChange={updateFilter}
              >
                <option value=''>All customers</option>
                {customers.map((customer) => (
                  <option
                    key={getValue(customer, ['customerid', 'id'])}
                    value={getValue(customer, ['customerid', 'id'])}
                  >
                    {getValue(customer, ['cust_name', 'name', 'company'])}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Order type
              <select
                name='orderType'
                value={filters.orderType}
                onChange={updateFilter}
              >
                <option value=''>All order types</option>
                {orderTypes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sales executive
              <select
                name='salesExecutive'
                value={filters.salesExecutive}
                onChange={updateFilter}
              >
                <option value=''>All sales executives</option>
                {salesExecutives.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Payment method
              <select
                name='paymentMethod'
                value={filters.paymentMethod}
                onChange={updateFilter}
              >
                <option value=''>All payment methods</option>
                {paymentMethods.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className='payments-query-form__actions'>
              <button
                className='payments-button payments-button--primary'
                type='submit'
              >
                Search
              </button>
              <button
                className='payments-button'
                type='button'
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </section>

      <section className='payments-summary-card' aria-label='Total payments'>
        <span className='app-content__eyebrow'>Collections</span>
        <span className='payments-summary-card__label'>
          Total payments received
        </span>
        <strong>{formatCurrency(totalPayments)}</strong>
      </section>

      <section
        className='payments-table-card'
        aria-labelledby='payments-table-title'
      >
        <div className='payments-table-card__header'>
          <div>
            <span className='app-content__eyebrow'>Payment register</span>
            <h2 id='payments-table-title'>Payment list</h2>
            <p>{payments.length} payments on this page</p>
          </div>
        </div>
        <div className='payments-table-wrap'>
          <table className='payments-table'>
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Order ID</th>
                <th>Order Type</th>
                <th>Entered By</th>
                <th>Method</th>
                <th>Amount</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='payments-table__empty' colSpan='9'>
                    Loading payments...
                  </td>
                </tr>
              ) : payments.length ? (
                payments.map((payment, index) => (
                  <tr key={`${getPaymentId(payment)}-${index}`}>
                    <td>
                      <strong>{getPaymentId(payment)}</strong>
                    </td>
                    <td>{formatDate(getPaymentDate(payment))}</td>
                    <td>
                      {getValue(payment, [
                        'customer_name',
                        'customerName',
                        'cust_name',
                        'customer',
                      ])}
                    </td>
                    <td>{getPaymentOrderId(payment)}</td>
                    <td>{getPaymentOrderType(payment)}</td>
                    <td>
                      {getValue(payment, [
                        'received_by_user',
                        'salesExecutive',
                        'sales_executive_id',
                        'sales_exec',
                      ])}
                    </td>
                    <td>
                      <span className='payments-status'>
                        {getPaymentMode(payment)}
                      </span>
                    </td>
                    <td className='payments-table__amount'>
                      {formatCurrency(getPaymentAmount(payment))}
                    </td>
                    <td>
                      <div className='payments-actions'>
                        <button
                          type='button'
                          onClick={() =>
                            navigate(
                              `/sales/orders/${getPaymentOrderId(payment)}`,
                            )
                          }
                        >
                          View order
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='payments-table__empty' colSpan='9'>
                    No payments match the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='payments-pagination'>
          <span>Showing up to 20 payments</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Payment
